export type FieldKind =
  | 'string'
  | 'number'
  | 'boolean'
  | 'enum'
  | 'date'
  | 'datetime'
  | 'object'
  | 'array';

import {DateTime} from 'luxon';

type IsDate<T> = T extends Date ? true : false;
type IsDateTime<T> = T extends DateTime ? true : false;
type IsArray<T> = T extends any[] ? true : false;
type IsBoolean<T> = T extends boolean ? true : false;
type IsNumber<T> = T extends number ? true : false;
type IsString<T> = T extends string ? true : false;

// string-literal enums end up here
type IsEnum<T> =
  T extends string
    ? string extends T
      ? false
      : true
    : false;

export type InferFieldKind<T> =
  IsDate<T> extends true ? 'date' :
    IsDateTime<T> extends true ? 'datetime' :
      IsArray<T> extends true ? 'array' :
        IsBoolean<T> extends true ? 'boolean' :
          IsNumber<T> extends true ? 'number' :
            IsEnum<T> extends true ? 'enum' :
              IsString<T> extends true ? 'string' :
                'object';

export type FieldTypeMap<T> = {
  [K in keyof T]: InferFieldKind<T[K]>;
};

export function resolveFieldTypes<T>(
  overrides: Partial<Record<keyof T, FieldKind>> = {},
  defaultType: FieldKind = 'string'
): FieldTypeMap<T> {

  return new Proxy({} as FieldTypeMap<T>, {
    get(_target, prop: string | symbol): FieldKind {

      // ignore symbols (e.g. util.inspect, iterator, etc.)
      if (typeof prop !== 'string') {
        return defaultType;
      }

      // narrow string → keyof T (safe at runtime)
      const key = prop as keyof T;

      return overrides[key] ?? defaultType;
    }
  });
}

